import { useEffect, useState } from "react";
import "./CompanyAgenda.scss";
import CreateJobModal from "../../../../Components/CreateJobModal/CreateJobModal";
import ApplicantsModal from "../ApplicantsModal/ApplicantsModal";
import { Pencil, X } from "lucide-react";
import { createJob, loadJobs, editJob, deleteJob } from "../../../../services/jobService";
import { getJobApplications } from "../../../../services/applicationService";
import { translateJobStatus, translateApplicationStatus } from "../../../../utils/statusLabels";
import { getDateOnly } from "../../../../utils/dateUtils";
import Toast from "../../../../Components/Toast/Toast"

const DAYS_PT = ["DOM.", "SEG.", "TER.", "QUA.", "QUI.", "SEX.", "SÁB."];
const MONTHS_PT = ["jan.", "fev.", "mar.", "abr.", "mai.", "jun.", "jul.", "ago.", "set.", "out.", "nov.", "dez."];

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diffToFri = (day - 5 + 7) % 7;
  d.setDate(d.getDate() - diffToFri);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDays(weekStart) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
}

function formatWeekRange(days) {
  const first = days[0];
  const last = days[days.length - 1];
  const fmt = (d) => `${String(d.getDate()).padStart(2, "0")} de ${MONTHS_PT[d.getMonth()]}`;
  return `${fmt(first)} a ${fmt(last)} de ${last.getFullYear()}`;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isSameWeek(weekStart, referenceWeekStart) {
  return weekStart.getTime() === referenceWeekStart.getTime();
}

function getLastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

const STATUS_LABEL = {
  open: { label: "Aguardando", cls: "open" },
  in_progress: { label: "Em andamento", cls: "in_progress" },
  completed: { label: "Finalizada", cls: "completed" },
  cancelled: { label: "Cancelada", cls: "cancelled" },
};

export default function CompanyAgenda({ isFreePlan, companyAddress, onSaveCompanyAddress, walletBalance, onOpenWallet }) {
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [applicantsJob, setApplicantsJob] = useState(null);
  const [applicantCounts, setApplicantCounts] = useState({}); // { [jobId]: number }
  const [toast, setToast] = useState({ message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };
  // CompanyAgenda.jsx — adicionar logo depois da função showToast
  const showJobToast = ({ title, description, variant }) => {
    showToast(description || title || "", variant === "destructive" ? "error" : "success");
  };

  const fetchJobs = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const data = await loadJobs(userId);
      setJobs(data);
      fetchApplicantCounts(data);
    } catch (error) {
      // console.error("Erro ao carregar vagas:", error);
    }
  };

  const fetchApplicantCounts = async (jobList) => {
    try {
      const counts = {};
      await Promise.all(
        jobList.map(async (job) => {
          try {
            const applicants = await getJobApplications(job.id);
            counts[job.id] = applicants.length;
          } catch {
            counts[job.id] = 0;
          }
        })
      );
      setApplicantCounts(counts);
    } catch (err) {
      // console.error("Erro ao buscar candidatos:", err);
    }
  };

  useEffect(() => { fetchJobs(); }, []);

  const getJobsForDay = (date) => {
    const dateStr = getDateOnly(date);
    return jobs.filter((job) => getDateOnly(job.date) === dateStr);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayWeekStart = getWeekStart(today);
  const [currentWeekStart, setCurrentWeekStart] = useState(todayWeekStart);
  const days = getWeekDays(currentWeekStart);
  const lastDayOfMonth = getLastDayOfMonth(days[days.length - 1]);
  const lastWeekStart = getWeekStart(lastDayOfMonth);
  const isOnTodayWeek = isSameWeek(currentWeekStart, todayWeekStart);
  const isOnLastWeek = currentWeekStart >= lastWeekStart;

  const goToToday = () => setCurrentWeekStart(new Date(todayWeekStart));
  const goPrev = () => {
    const prev = new Date(currentWeekStart);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeekStart(prev);
  };
  const goNext = () => {
    if (!isOnLastWeek) {
      const next = new Date(currentWeekStart);
      next.setDate(next.getDate() + 7);
      setCurrentWeekStart(next);
    }
  };

  const handleCreateJob = async (jobData) => {
    const userId = localStorage.getItem("userId");
    const newJob = await createJob(userId, jobData);
    await fetchJobs();
    return newJob;
  };

  const handleEditJob = async (jobId, jobData) => {
    const result = await editJob(jobId, jobData);
    await fetchJobs();
    return result;
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await deleteJob(jobId);
      showToast('Vaga excluída com sucesso.', 'success');
      await fetchJobs();
    } catch (error) {
      console.error(error);
      showToast('Não foi possível excluir a vaga. Tente novamente.', 'error');
    }
  };

  return (
    <div className="agenda-wrapper">
      <div className="agenda-card">

        {/* ── Header ── */}
        <div className="agenda-header">
          <div className="agenda-title-group">
            <div className="agenda-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div>
              <h1 className="agenda-title">Agenda da Semana</h1>
              <p className="agenda-subtitle">{formatWeekRange(days)}</p>
            </div>
          </div>

          <div className="agenda-controls">
            <button className={`btn-hoje ${isOnTodayWeek ? "btn-hoje--active" : ""}`} onClick={goToToday}>
              Hoje
            </button>
            <div className="agenda-nav">
              <button className="nav-btn" onClick={goPrev} aria-label="Semana anterior">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button className={`nav-btn ${isOnTodayWeek ? "nav-btn--active" : ""}`} onClick={goNext} disabled={isOnLastWeek} aria-label="Próxima semana">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
            {/* <button className="btn-nova-vaga" onClick={() => setShowCreateJobModal(true)}>
              + Nova Vaga
            </button> */}
          </div>
        </div>

        {/* ── Grid ── */}
        <div className="agenda-grid">
          {days.map((date) => {
            const isToday = isSameDay(date, today);
            const dayName = DAYS_PT[date.getDay()];
            const dayNum = date.getDate();
            const monthName = MONTHS_PT[date.getMonth()];
            const dayJobs = getJobsForDay(date);

            return (
              <div key={date.toISOString()} className={`day-column ${isToday ? "day-column--today" : ""}`}>

                {/* Day header */}
                <div className="day-header">
                  <div className="day-label">
                    <span className="day-name">{dayName}</span>
                    <span className="day-date">
                      <strong>{dayNum}</strong> {monthName.toUpperCase()}
                    </span>
                  </div>
                  <button
                    className="add-btn"
                    aria-label={`Adicionar vaga em ${dayNum}`}
                    onClick={() => {
                      setSelectedDate(getDateOnly(date));
                      setShowCreateJobModal(true);
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                </div>

                {/* Day body */}
                <div className="day-body">
                  {dayJobs.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <circle cx="12" cy="12" r="9" />
                          <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                      </div>
                      <span className="empty-text">Nenhuma vaga</span>
                    </div>
                  ) : (
                    dayJobs.map((job) => {
                      const st = STATUS_LABEL[job.status] ?? { label: job.status, cls: "open" };
                      const count = applicantCounts[job.id] ?? 0;

                      return (
                        <div key={job.id} className="job-card">
                          {/* título + status */}
                          <div className="job-card-header">
                            <span className="job-title">{job.title}</span>
                            <span className="job-status job-status--">{translateJobStatus(job.status)}</span>
                          </div>

                          {/* horário */}
                          <div className="job-card-time">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                            </svg>
                            <span>{job.startTime} – {job.endTime}</span>
                          </div>

                          {/* candidatos */}
                          <button
                            className={`job-card-applicants ${count > 0 ? "job-card-applicants--has" : ""}`}
                            onClick={() => setApplicantsJob(job)}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            {count > 0
                              ? <><strong>{count}</strong> candidato{count !== 1 ? "s" : ""} · Ver</>
                              : "Nenhum candidato"}
                          </button>

                          {/* ações editar/excluir */}
                          <div className="job-card-actions">
                            <button
                              className="job-btn job-btn--edit"
                              onClick={() => { setSelectedJob(job); setShowEditJobModal(true); }}
                            >
                              <Pencil size={13} /> Editar
                            </button>
                            <button className="job-btn job-btn--delete" onClick={() => handleDeleteJob(job.id)}>
                              <X />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      </div>

      {/* Modais */}
      {showCreateJobModal && (
        <CreateJobModal
          open={showCreateJobModal}
          onClose={() => {
            setShowCreateJobModal(false);
            setSelectedDate(null);
          }}
          criarVaga={handleCreateJob}
          initialDate={selectedDate}
          isFreePlan={isFreePlan}
          companyAddress={companyAddress}
          onSaveCompanyAddress={onSaveCompanyAddress}
          walletBalance={walletBalance}   // ⬅️ NOVO
          onOpenWallet={onOpenWallet}       // ⬅️ NOVO
          toast={showJobToast}
        />
      )}

      {showEditJobModal && selectedJob && (
        <CreateJobModal
          open={showEditJobModal}
          onClose={() => {
            setShowEditJobModal(false);
            setSelectedJob(null);
          }}
          initialDate={selectedJob.date?.split("T")[0]}
          initialData={selectedJob}
          criarVaga={async (jobData) => {
            await handleEditJob(selectedJob.id, jobData);
          }}
          isEditing
          isFreePlan={isFreePlan}
          companyAddress={companyAddress}
          onSaveCompanyAddress={onSaveCompanyAddress}
          walletBalance={walletBalance}
          onOpenWallet={onOpenWallet} 
          toast={showJobToast}
        />
      )}

      {applicantsJob && (
        <ApplicantsModal
          job={applicantsJob}
          onClose={() => setApplicantsJob(null)}
        />
      )}
    </div>
  );
}